import { API_ENDPOINTS } from '../constants/endpoints.ts';

interface IntercomInstallResponse {
	authUrl?: string;
	success?: boolean;
}

interface IntercomOwnAppSetupResponse {
	webhookUrl?: string;
	success?: boolean;
}

interface IntercomOwnAppConnectResponse {
	appId?: string;
	success?: boolean;
}

/** A teammate of the connected workspace that Kim can be given. */
export interface IntercomTeammate {
	id: string;
	name: string;
	email: string;
}

/** The workspace's teammates and the one already picked for Kim; `selectedId` is null when none is. */
export interface IntercomTeammates {
	teammates: IntercomTeammate[];
	selectedId: string | null;
}

interface IntercomTeammatesResponse {
	teammates?: IntercomTeammate[];
	selectedId?: string | null;
	success?: boolean;
}

interface IntercomSelectTeammateResponse {
	teammate?: IntercomTeammate;
	success?: boolean;
}

/** What the customer copies out of their own Intercom app. */
export interface IntercomOwnAppCredentials {
	accessToken: string;
	clientSecret: string;
}

interface IntercomHttpClient {
	get<T = unknown>(url: string): Promise<{ data: T }>;
	post<T = unknown>(url: string, body?: unknown): Promise<{ data: T }>;
}

/**
 * Intercom is a helpdesk, so its connection belongs to the account: no brand is sent.
 * Disconnect is the generic `/integrations/:id/disconnect` every provider shares.
 */
export const createIntercomIntegrationService = (client: IntercomHttpClient) => ({
	/** Where to send the operator to authorize Kim in Intercom. */
	async getIntercomInstallUrl(): Promise<string> {
		const { data } = await client.get<IntercomInstallResponse>('/machine' + API_ENDPOINTS.INTERCOM.INSTALL);
		if (!data?.authUrl) {
			throw new Error('Intercom install response had no authUrl');
		}
		return data.authUrl;
	},

	/** The webhook URL the customer registers on their own Intercom app. */
	async getIntercomOwnAppWebhookUrl(): Promise<string> {
		const { data } = await client.get<IntercomOwnAppSetupResponse>('/machine' + API_ENDPOINTS.INTERCOM.OWN_APP_SETUP);
		if (!data?.webhookUrl) {
			throw new Error('Intercom own-app setup response had no webhookUrl');
		}
		return data.webhookUrl;
	},

	/** Connect through the customer's own Intercom app; resolves to the workspace id. */
	async connectIntercomWithOwnApp({ accessToken, clientSecret }: IntercomOwnAppCredentials): Promise<string> {
		const { data } = await client.post<IntercomOwnAppConnectResponse>('/machine' + API_ENDPOINTS.INTERCOM.CONNECT, {
			access_token: accessToken,
			client_secret: clientSecret
		});
		if (!data?.success || !data.appId) {
			throw new Error('Intercom own-app connect response had no appId');
		}
		return data.appId;
	},

	/** The connected workspace's teammates, and the one Kim already works as. */
	async getIntercomTeammates(): Promise<IntercomTeammates> {
		const { data } = await client.get<IntercomTeammatesResponse>('/machine' + API_ENDPOINTS.INTERCOM.TEAMMATES);
		if (!data?.success || !Array.isArray(data.teammates)) {
			throw new Error('Intercom teammates response had no teammates');
		}
		return { teammates: data.teammates, selectedId: data.selectedId ?? null };
	},

	/** Pick the teammate Kim works as; resolves to the teammate machine saved. */
	async selectIntercomKimTeammate(adminId: string): Promise<IntercomTeammate> {
		const { data } = await client.post<IntercomSelectTeammateResponse>('/machine' + API_ENDPOINTS.INTERCOM.TEAMMATE, {
			admin_id: adminId
		});
		if (!data?.success || !data.teammate) {
			throw new Error('Intercom teammate response had no teammate');
		}
		return data.teammate;
	}
});
